import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button, Buttons } from '../forms/Button';
import { Select } from '../forms/Select';
import { motions } from '../motions/motions';
import { MonthView } from './MonthView';
import { QuarterView } from './QuarterView';
import { timeFunctions } from './util';
import { WeekView } from './WeekView';
import { YearView } from './YearView';

/**
 * Views supported by the calendar.
 */
export type CalendarView = 'year' | 'quarter' | 'month' | 'week';
const views = Object.freeze(['year', 'quarter', 'month', 'week'] as CalendarView[]);

/**
 * Calendar task event.
 *
 * @member name the event name
 * @member group event group used to aggregate events in some views
 * @member color event highlight color
 * @member interval event start and end dates (both inclusive at day resolution)
 * @member draggable allows the event to be dragged (dragging also requires `onDnD` to be provided to the calendar)
 * @member key optional key linked to the event usually used in drag and drop
 * @member data data linked to the calender event
 * @member render custom event render function, it takes the event, current view, tooltip, and returns a React node
 */
export type CalendarEvent<T = any> = {
    name: string;
    group: string;
    color: string;
    interval: Interval;
    draggable?: boolean;
    key?: number | string;
    data?: T;
    render?: (props: { event: CalendarEvent<T>; view: CalendarView; tooltip: boolean }) => React.ReactNode;
};

/**
 * The calendar context stores data and functions shared among all calendar sub-components.
 * Check the `Calendar` documentation for more details.
 */
export type CalendarContext = {
    events: CalendarEvent<any>[];
    view: CalendarView;
    anchor: Date;
    intervals: Record<CalendarView, Interval>;
    setView: (view: CalendarView) => void;
    setAnchor: (anchor: Date) => void;
    onFilter?: (events: CalendarEvent<any>[]) => void;
    onClick?: (event: CalendarEvent<any>) => void;
    onDnD?: (event: CalendarEvent<any>, duration: Duration) => void;
    onExternalDnD?: (event: React.DragEvent, date: Date) => void;
    groupSort?: (a: string, b: string) => number;
};

const useCalendarClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        borderRadius: 5,
        backgroundColor: 'white',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 16,
        padding: 6,
        borderBottom: `1px solid ${theme.colors.neutral[6]}`,
    },
    view: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
});

/**
 * Custom calendar widget.
 * This calendar supports any type of events, the event elements are passed through objects that conform the
 * `CalendarEvent` type.
 * The calendar manages the `view` and `anchor` states internally (accepting only `initialView` and `initialAnchor`)
 * working like an uncontrolled component from the caller perspective, the `view` and `anchor` states can still be
 * changed from outside using the `setView` and `setAnchor` references, which will be populated in the first
 * `useLayoutEffect` call.
 * For caller components, `event` elements behave like a controlled input, the calendar will memoize filtered versions
 * of `events` but the filters will be cleared when `events` change (or when `view` and `interval` change internally).
 *
 * About rendering custom events:
 * The default rendering is very simple, only displaying the event name.
 * `CalendarEvent` allows custom renders by setting its `render` function property.
 * The `render` function takes the `CalendarEvent` as a parameter and return a React node.
 * The `CalendarEvent` can also carry any information in its `data` property, which can be also be accessed by `render`.
 *
 * @param loading indicates a loading state in the calendar
 * @param events list of events to display
 * @param initialView initial calendar view, defaults to `'month'`
 * @param initialAnchor initial calendar anchor, defaults to current day
 * @param onViewChange callback triggered the view changes
 * @param onAnchorChange callback triggered the anchor change
 * @param onIntervalChange callback triggered when the time interval changes
 * @param onFilter callback triggered when the events displayed in the view change
 * @param onClick callback triggered when an event is clicked
 * @param onDnD callback triggered when an event is dragged and dropped in another date
 * @param onExternalDnD callback triggered when content is dropped from outside the calendar, the presence of this
 *        property also enables drag effects for external elements
 * @param groupSort function to sort groups used in some views, if not provided, lexicographical order is used
 * @param header content to be rendered in the header
 */
export const Calendar = <T,>({
    events,
    initialView,
    initialAnchor,
    setViewRef,
    setAnchorRef,
    onViewChange,
    onAnchorChange,
    onIntervalChange,
    onFilter,
    onClick,
    onDnD,
    onExternalDnD,
    groupSort,
    header,
    ...rootProps
}: {
    loading?: boolean;
    events: CalendarEvent<T>[];
    initialView?: CalendarView;
    initialAnchor?: Date;
    setViewRef?: React.MutableRefObject<(view: CalendarView) => void>;
    setAnchorRef?: React.MutableRefObject<(anchor: Date) => void>;
    onViewChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onAnchorChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onIntervalChange?: (view: CalendarView, anchor: Date, interval: Interval) => void;
    onFilter?: (events: CalendarEvent<T>[]) => void;
    onClick?: (event: CalendarEvent<T>) => void;
    onDnD?: (event: CalendarEvent<T>, duration: Duration) => void;
    onExternalDnD?: (event: React.DragEvent, date: Date) => void;
    groupSort?: (a: string, b: string) => number;
    header?: React.ReactNode;
} & JSX.IntrinsicElements['div']) => {
    const classes = useCalendarClasses();
    const [context] = React.useState(() => React.createContext<CalendarContext>({} as any));
    const [view, setView] = React.useState(initialView ?? 'month');
    const [anchor, setAnchor] = React.useState(initialAnchor ?? new Date());
    const intervals = Object.entries(timeFunctions)
        .map(([name, { start, end }]) => [name, { start: start(anchor), end: end(anchor) }] as [CalendarView, Interval])
        .reduce<Record<CalendarView, Interval>>((acc, [name, interval]) => ((acc[name] = interval), acc), {} as any);
    const View = { week: WeekView, month: MonthView, quarter: QuarterView, year: YearView }[view];

    React.useLayoutEffect(() => void (setViewRef && (setViewRef.current = setView)), [setView, setViewRef]);
    React.useLayoutEffect(() => void (setAnchorRef && (setAnchorRef.current = setAnchor)), [setAnchor, setAnchorRef]);
    React.useEffect(() => onViewChange?.(view, anchor, intervals[view]), [view]);
    React.useEffect(() => onAnchorChange?.(view, anchor, intervals[view]), [+anchor]);
    React.useEffect(() => onIntervalChange?.(view, anchor, intervals[view]), [
        +intervals[view].start,
        +intervals[view].end,
    ]);

    return (
        <context.Provider
            value={{
                events,
                anchor,
                setAnchor,
                view,
                setView,
                intervals,
                onFilter,
                onClick,
                onDnD,
                onExternalDnD,
                groupSort,
            }}
        >
            <div {...rootProps} className={clsx(classes.root, rootProps.className)}>
                <div className={classes.header}>
                    <Navigator context={context} />
                    {header}
                    <ViewChooser context={context} />
                </div>
                <AnimatePresence exitBeforeEnter>
                    <motion.div key={view} className={classes.view} {...motions.presence({ pop: 10 })}>
                        <View context={context} />
                    </motion.div>
                </AnimatePresence>
            </div>
        </context.Provider>
    );
};

const useNavigatorClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        gap: 8,
        color: theme.colors.neutral[1],
        ...theme.fonts.h5,
        whiteSpace: 'nowrap',
    },
    button: {
        color: theme.colors.neutral[1],
        '& > svg': { color: theme.colors.neutral[3] },
    },
});

/**
 * Component that controls the calendar anchor.
 *
 * @param props.context calendar context
 */
const Navigator = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useNavigatorClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.Calendar.Navigator' });
    const { view, anchor, setAnchor, intervals } = React.useContext(props.context);
    const { add } = timeFunctions[view];

    return (
        <div className={classes.root}>
            <Buttons buttonProps={{ className: classes.button, icon: true }}>
                <Button onClick={() => setAnchor(add(anchor, -1))}>
                    <ChevronLeft />
                </Button>
                <Button onClick={() => setAnchor(new Date())}>{t('today')}</Button>
                <Button onClick={() => setAnchor(add(anchor, 1))}>
                    <ChevronRight />
                </Button>
            </Buttons>
            <span>{t(`date-${view}`, { anchor, ...intervals[view] })}</span>
        </div>
    );
};

const useViewChooserClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        color: theme.colors.neutral[10],
        ...theme.fonts.h6,
        whiteSpace: 'nowrap',
    },
    select: {
        color: theme.colors.neutral[1],
        ...theme.fonts.button,
    },
});

/**
 * Component that displays and chooses the current calendar view.
 *
 * @param props.context calendar context
 */
const ViewChooser = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useViewChooserClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.Calendar.ViewChooser' });
    const { view, setView } = React.useContext(props.context);

    return (
        <div className={classes.root}>
            <span>{t('view-by')}</span>
            <Select
                insideProps={{ className: classes.select }}
                theme='light'
                options={[...views]}
                selected={views.map(v => v === view)}
                onSelected={selected => setView(views.filter((_, i) => selected[i])[0])}
                render={v => t(`view-${v}`)}
            />
        </div>
    );
};
